/**
 * appShellCache — track-backlog--perf-indexeddb-app-shell-cache
 *
 * Thin key-value wrapper over raw IndexedDB (no new dependency) used to
 * render the app shell instantly from a prior session's data while the
 * live Firebase Auth → workspace membership → Team Log chain resolves in
 * the background. Every read/write is best-effort: IndexedDB is
 * unavailable in some contexts (private browsing, disabled storage) and
 * a cache miss/failure must never block the real data path, so failures
 * resolve to `null` / no-op rather than throwing.
 *
 * See seq #26989 for the full spec.
 */
import type { TeamLogMessage } from "../types";

const DB_NAME = "relay-app-shell-cache";
const DB_VERSION = 1;
const STORE = "kv";

function openCacheDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet<T>(key: string): Promise<T | null> {
  try {
    const db = await openCacheDB();
    return await new Promise<T | null>((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve((req.result as T) ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function idbSet<T>(key: string, value: T): Promise<void> {
  try {
    const db = await openCacheDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // Cache write failures are non-fatal — the live Firestore path is
    // still the source of truth.
  }
}

export interface CachedTeamLog {
  messages: TeamLogMessage[];
  updatedAt: number;
}

export function getCachedTeamLog(workspaceId: string): Promise<CachedTeamLog | null> {
  return idbGet<CachedTeamLog>(`team-log:${workspaceId}`);
}

export function setCachedTeamLog(workspaceId: string, messages: TeamLogMessage[]): Promise<void> {
  return idbSet<CachedTeamLog>(`team-log:${workspaceId}`, { messages, updatedAt: Date.now() });
}

export interface CachedMembership {
  role: "lead" | "partner";
  workspaceId: string;
}

export function getCachedMembership(email: string): Promise<CachedMembership | null> {
  return idbGet<CachedMembership>(`membership:${email}`);
}

export function setCachedMembership(email: string, membership: CachedMembership): Promise<void> {
  return idbSet<CachedMembership>(`membership:${email}`, membership);
}

export interface CachedAttentionCount {
  count: number;
  tier1Count: number;
}

export function getCachedAttentionCount(workspaceId: string): Promise<CachedAttentionCount | null> {
  return idbGet<CachedAttentionCount>(`attention:${workspaceId}`);
}

export function setCachedAttentionCount(workspaceId: string, data: CachedAttentionCount): Promise<void> {
  return idbSet<CachedAttentionCount>(`attention:${workspaceId}`, data);
}
